import { useEffect, useMemo, useState } from 'react';
import { Loader2, MessageSquare, Star, X } from 'lucide-react';
import { api } from '../../lib/api';

export interface ReviewableOrder {
  id: string;
  restaurantId: string;
  restaurantName?: string;
  rating?: number;
}

interface OrderReviewModalProps {
  isOpen: boolean;
  order: ReviewableOrder | null;
  onClose: () => void;
  onSubmitted?: (payload: { orderId: string; restaurantId: string; rating: number; comment: string }) => void;
  onRequireLogin?: () => void;
}

const REVIEW_STORAGE_KEY = 'submitted_order_reviews';

const persistReviewLocally = (orderId: string, payload: { rating: number; comment: string }) => {
  const existing = JSON.parse(localStorage.getItem(REVIEW_STORAGE_KEY) || '{}');
  existing[orderId] = {
    rating: payload.rating,
    comment: payload.comment,
    submittedAt: new Date().toISOString(),
  };
  localStorage.setItem(REVIEW_STORAGE_KEY, JSON.stringify(existing));
};

const StarRatingInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) => (
  <div>
    <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-1 transition-transform hover:scale-110"
        >
          <Star
            className={`w-6 h-6 ${
              star <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  </div>
);

export function OrderReviewModal({ isOpen, order, onClose, onSubmitted, onRequireLogin }: OrderReviewModalProps) {
  const initialRating = useMemo(() => Number(order?.rating || 0), [order?.rating]);
  const [rating, setRating] = useState(initialRating);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const nextRating = Number(order?.rating || 0);
    setRating(nextRating);
    setComment('');
    setError('');
    setSubmitting(false);
  }, [order?.id, order?.rating]);

  if (!isOpen || !order) return null;

  const handleSubmit = async () => {
    if (!order.restaurantId) {
      setError('Restaurant information missing for this order.');
      return;
    }

    if (rating < 1) {
      setError('Please select at least one star.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      await api.reviews.create({
        restaurantId: order.restaurantId,
        orderId: order.id,
        rating,
        comment: comment.trim(),
      });

      persistReviewLocally(order.id, { rating, comment: comment.trim() });
      onSubmitted?.({ orderId: order.id, restaurantId: order.restaurantId, rating, comment: comment.trim() });
      onClose();
    } catch (submitError: any) {
      const message =
        String(submitError?.message || '').trim() || 'Failed to submit review. Please try again.';
      if (message.includes('[401]')) {
        onClose();
        onRequireLogin?.();
        return;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold">Rate Your Order</h2>
              <p className="text-blue-100 text-sm mt-1">{order.restaurantName || 'Restaurant'}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <StarRatingInput
            label="Overall rating"
            value={rating}
            onChange={setRating}
          />

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Review comment
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Tell us about your experience..."
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
